
import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2, X } from 'lucide-react';
import { useCreateCustomerForm } from './hooks/useCreateCustomerForm';

interface CreateCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCustomerCreated: (customerId: string, customerName: string) => void;
  onCancel: () => void;
  editCustomerId?: string | null;
  preselectedShopId?: string;
}

const CreateCustomerDialog = ({
  open,
  onOpenChange,
  onCustomerCreated,
  onCancel,
  editCustomerId = null,
  preselectedShopId
}: CreateCustomerDialogProps) => {
  // Utilisation du hook avec les bons props
  const {
    name, setName,
    email, setEmail,
    phone, setPhone,
    errors,
    isSubmitting,
    handleSave,
    loadCustomerData
  } = useCreateCustomerForm({
    onCustomerCreated,
    onCancel,
    editCustomerId,
    onClientUpdated: undefined,
    initialClient: null,
    preselectedShopId
  });
  
  // Charger les données du client si c'est une édition
  useEffect(() => {
    if (open && editCustomerId) {
      loadCustomerData();
    }
  }, [open, editCustomerId]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSave();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{editCustomerId ? "Modifier le client" : "Créer un nouveau client"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom <span className="text-red-500">*</span></Label>
            <Input 
              id="name"
              placeholder="Nom du client"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email"
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone <span className="text-red-500">*</span></Label>
            <Input 
              id="phone"
              placeholder="+33 6 12 34 56 78"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {errors.phone && (
              <p className="text-sm text-red-500 font-medium">{errors.phone}</p>
            )}
          </div>
          
          <DialogFooter className="flex justify-between mt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <X className="mr-2 h-4 w-4" />
              Annuler
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2 rounded-md"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {editCustomerId ? "Mise à jour..." : "Création..."}
                </>
              ) : (
                editCustomerId ? "Mettre à jour" : "Créer le client"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerDialog;
